<template>
  <div class="toggle-switch">
    <label
      :class="{
        'is-icon-exiting': iconExiting,
        'is-icon-entering': iconEntering
      }"
    >
      <input
        type="checkbox"
        :checked="season === 'summer'"
        @click.stop="onSeasonClick"
      />
      <span class="slider"></span>
      <p
        class="text-[10px] text-toggle transition-all duration-300 uppercase left-[5px] hidden lg:block"
        :class="season === 'summer' ? '' : 'opacity-0'"
      >
        {{ $text('summer') }}
      </p>
      <p
        class="text-[10px] text-toggle text-winter transition-all duration-300 uppercase hidden lg:block"
        :class="season === 'winter' ? '' : 'opacity-0'"
      >
        {{ $text('winter') }}
      </p>
    </label>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useSettingsStore } from '~/stores/useSettingsStore';

const router = useRouter();
const { $fullpath, $text } = useSiteSettings();
const settingsStore = useSettingsStore();
const { siteParts } = storeToRefs(settingsStore);
const cookieSeason = useCookie('season');
const season = computed(() => cookieSeason.value || siteParts.value?.header.season || 'no-season');

const iconExiting = ref(false);
const iconEntering = ref(false);

/** Mobile exit transition — keep in sync with CSS `.slider::before` opacity/transform duration */
const EXIT_MS = 420;
/** Mobile enter keyframes — keep in sync with `season-icon-in-mobile` duration */
const ENTER_MS = 350;

const isDesktop = () =>
  typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;

/**
 * Desktop: no preventDefault — matches origin/master so the checkbox + :checked ~
 * CSS updates in sync and the knob transform can transition.
 * Mobile: preventDefault so the checkbox doesn’t flip before our exit animation runs.
 */
const onSeasonClick = (e: MouseEvent) => {
  if (isDesktop()) {
    toggleSeason();
    return;
  }
  e.preventDefault();
  void runMobileSeasonToggle();
};

const runMobileSeasonToggle = async () => {
  if (iconExiting.value || iconEntering.value) {
    return;
  }
  iconExiting.value = true;
  await new Promise<void>((resolve) => setTimeout(resolve, EXIT_MS));
  toggleSeason();
  iconExiting.value = false;

  iconEntering.value = true;
  await new Promise<void>((resolve) => setTimeout(resolve, ENTER_MS));
  iconEntering.value = false;
};

const toggleSeason = () => {
  let url: string = '';
  if (season.value === 'summer') {
    url = $fullpath(siteParts.value?.header.winterPageLink._ref || '/');

    // season.value = 'winter';
    cookieSeason.value = 'winter';
  } else if (season.value === 'winter') {
    url = $fullpath(siteParts.value?.header.summerPageLink._ref || '/');
    // season.value = 'summer';
    cookieSeason.value = 'summer';
  }

  setTimeout(async () => {
    await router.push(url === '' ? '' : url);
  }, 200);
};
</script>

<style lang="postcss" scoped>
.toggle-switch {
  --height-btn: 42px;
  /* Mobile: circle; lg+: same width as origin/master */
  --width-btn: 42px;
  --height-icon: 24px;
  --padding-icon: 6px;
  height: var(--height-btn);
  @apply ml-2 lg:ml-3;
  position: relative;
  width: var(--width-btn);
}

@media (min-width: 1024px) {
  .toggle-switch {
    --width-btn: 110px;
  }
}

.text-toggle {
  @apply h-fit px-2;
  position: absolute;
  top: 0;
  bottom: 0;
  margin: auto 0;
}

.text-winter {
  @apply right-[12px];
}

label {
  @apply border-[#93A1AF] border-[1px] border-opacity-20;
  position: absolute;
  width: 100%;
  height: var(--height-btn);
  border-radius: 50%;
  cursor: pointer;
  font-size: 10px;
}

@media (min-width: 1024px) {
  label {
    border-radius: 50px;
  }
}

input {
  position: absolute;
  display: none;
}

.slider {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  transition: 0.3s;
}

@media (min-width: 1024px) {
  .slider {
    border-radius: 50px;
  }
}

.slider::before {
  content: url('../assets/svg/winter.svg');
  position: absolute;
  /* Mobile: centered icon in circle */
  top: 50%;
  left: 50%;
  display: block;
  width: var(--height-icon);
  height: var(--height-icon);
  border-radius: 50%;
  opacity: 1;
  transform: translate(-50%, -50%);
  transition:
    opacity 0.42s ease,
    transform 0.42s ease;
}

input:checked ~ .slider::before {
  content: url('../assets/svg/sun.svg');
  box-shadow: none;
}

/* Mobile: exit through top */
@media (max-width: 1023px) {
  label.is-icon-exiting .slider::before {
    opacity: 0;
    transform: translate(-50%, -50%) translateY(-14px);
  }
}

/* Mobile: enter from below */
@media (max-width: 1023px) {
  label.is-icon-entering .slider::before {
    transition: none;
    animation: season-icon-in-mobile 0.35s ease forwards;
  }
}

@keyframes season-icon-in-mobile {
  from {
    opacity: 0;
    transform: translate(-50%, -50%) translateY(14px);
  }

  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
}

/* lg+: identical to origin/master — slide only, no fade choreography */
@media (min-width: 1024px) {
  .slider::before {
    top: calc((var(--height-btn) - var(--height-icon)) / 2);
    left: var(--padding-icon);
    transform: none;
    transition: transform 0.3s ease;
  }

  input:checked ~ .slider::before {
    transform: translateX(calc(var(--width-btn) - var(--height-icon) - 3 * var(--padding-icon)));
  }
}
</style>
